import type { Numeric, NumericValue } from '@/utils/numeric';

// ============================================================================
// Equipment Module TypeScript Types
// ============================================================================

export type EquipmentStatus = 'active' | 'inactive' | 'under_repair' | 'retired';
export type MeterType = 'hours' | 'km' | 'both' | 'none';
export type IntervalType = 'hours' | 'km' | 'days';
export type SessionStatus = 'pre_start' | 'active' | 'completed' | 'cancelled';
export type AuRegoState = 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'NT' | 'ACT';
export type FuelType = 'diesel' | 'petrol' | 'electric' | 'hybrid' | 'lpg';
export type TransmissionType = 'manual' | 'auto' | 'cvt' | 'dct';
export type DriveType = '2wd' | '4wd' | 'awd';

// ─── Categories ──────────────────────────────────────────────────────────────
//
// What KIND of thing a machine is. It lives on the type's registry row
// (equipment_type_registry.category) and arrives on every machine as
// `equipment_category`, so a custom type an org invents joins the right
// pickers and cards like a built-in one. The pages switch on the category —
// never on a local list of type keys, which is how the old lists drifted
// (the form's sprayer list had no drone in it).

export type EquipmentCategory =
  | 'vehicle' | 'machinery' | 'implement' | 'sprayer'
  | 'pump' | 'handheld' | 'plant' | 'other';

export const EQUIPMENT_CATEGORIES: { key: EquipmentCategory; label: string }[] = [
  { key: 'vehicle',   label: 'Vehicles' },
  { key: 'machinery', label: 'Tractors & machinery' },
  { key: 'implement', label: 'Implements' },
  { key: 'sprayer',   label: 'Sprayers' },
  { key: 'pump',      label: 'Pumps' },
  { key: 'handheld',  label: 'Hand tools' },
  { key: 'plant',     label: 'Generators & plant' },
  { key: 'other',     label: 'Other' },
];

export function categoryLabel(category: EquipmentCategory): string {
  return EQUIPMENT_CATEGORIES.find(c => c.key === category)?.label ?? 'Other';
}

// The built-in types' categories, for a machine that arrives without
// `equipment_category` (a payload from before the column, or a partial shape
// such as a spray's equipment summary). Mirrors the seed in
// HHNodeServer/src/migrations/20260928-01-equipment-type-categories.sql; the
// server's answer always wins when present.
const BUILTIN_CATEGORY: Record<string, EquipmentCategory> = {
  tractor: 'machinery', loader: 'machinery', forklift: 'machinery',
  ute: 'vehicle', truck: 'vehicle', car: 'vehicle', van: 'vehicle',
  quad_atv: 'vehicle', vehicle: 'vehicle',
  airblast_sprayer: 'sprayer', boom_sprayer: 'sprayer', spray_tank: 'sprayer',
  knapsack: 'sprayer', drone: 'sprayer',
  slasher: 'implement', mower: 'implement', auger: 'implement',
  hay_equipment: 'implement', other_implement: 'implement',
  irrigation_pump: 'pump', bore_pump: 'pump', transfer_pump: 'pump',
  string_trimmer: 'handheld', chainsaw: 'handheld', pole_saw: 'handheld',
  hedge_trimmer: 'handheld', blower: 'handheld', pruning_shears: 'handheld',
  generator: 'plant', compressor: 'plant', pressure_washer: 'plant',
};

export function categoryOf(eq: { equipment_type: string; equipment_category?: EquipmentCategory | null }): EquipmentCategory {
  return eq.equipment_category ?? BUILTIN_CATEGORY[eq.equipment_type] ?? 'other';
}

// Machines that carry a spray calibration — towed, mounted or self-propelled
// alike. One predicate, shared by the equipment pages, the spray picker and
// the calibration page.
export function isSprayer(eq: { equipment_type: string; equipment_category?: EquipmentCategory | null }): boolean {
  return categoryOf(eq) === 'sprayer';
}

// Machines the road-vehicle details card (VIN, rego, engine) applies to.
export function isRoadVehicleCategory(category: EquipmentCategory): boolean {
  return category === 'vehicle' || category === 'machinery';
}

// ─── Equipment ───────────────────────────────────────────────────────────────

export interface EquipmentTypeOption {
  type_key: string;
  label: string;
  is_builtin: boolean;
  /** Absent only from an older server — read through categoryOf. */
  category?: EquipmentCategory;
  default_meter_type?: MeterType;
  default_is_implement?: boolean;
  default_can_tow?: boolean;
}

/** A row on Equipment Settings → Types: the type plus what hangs off it. */
export interface ManagedEquipmentType extends EquipmentTypeOption {
  category: EquipmentCategory;
  /** An org row sharing a built-in's key — left over from before custom types refused that. */
  shadows_builtin: boolean;
  machine_count: number;
  template_count: number;
}

export interface EquipmentTypeInput {
  label: string;
  category: EquipmentCategory;
  default_meter_type: MeterType;
  default_is_implement: boolean;
  default_can_tow: boolean;
}

export interface EquipmentImplementSummary {
  id: number;
  attachment_id: number;
  name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  attached_at?: string | null;
  status?: EquipmentStatus;
}

export interface Equipment {
  id: number;
  organisation_id: number;
  name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  make?: string | null;
  model?: string | null;
  serial_number?: string | null;
  year_of_manufacture?: number | null;
  purchase_date?: string | null;
  purchase_price?: Numeric;
  status: EquipmentStatus;
  location_name?: string | null;
  farm_id?: number | null;
  farm_name?: string | null;
  meter_type: MeterType;
  current_hours?: Numeric;
  current_km?: Numeric;
  total_hours_logged: NumericValue;
  can_tow_implements: boolean;
  is_implement: boolean;
  working_width_m?: Numeric;
  // Spray calibration (for sprayer types)
  tank_capacity_litres?: Numeric;
  nozzle_type?: string | null;
  nozzle_spacing_cm?: Numeric;
  boom_width_m?: Numeric;
  operating_pressure_bar?: Numeric;
  operating_speed_kph?: Numeric;
  output_rate_lpm?: Numeric;
  output_rate_lpha?: Numeric;
  calibration_date?: string | null;
  calibration_notes?: string | null;
  // Road-vehicle fields
  vin?: string | null;
  rego_number?: string | null;
  rego_state?: AuRegoState | null;
  rego_expiry?: string | null;
  /** Not insured on purpose: never flagged as having no insurance. */
  insurance_not_required?: boolean;
  engine_number?: string | null;
  engine_code?: string | null;
  engine_capacity_l?: Numeric;
  fuel_type?: FuelType | null;
  transmission?: TransmissionType | null;
  drive_type?: DriveType | null;
  body_type?: string | null;
  colour?: string | null;
  notes?: string | null;
  // Computed (list endpoint only)
  active_session_id?: number | null;
  active_session_operator?: string | null;
  current_implements?: EquipmentImplementSummary[] | null;
  /** Detail endpoint only — the prime mover this implement is hitched to, if any. */
  attached_to?: EquipmentImplementSummary | null;
  maintenance_due_count?: number;
  last_service_title?: string | null;
  last_service_date?: string | null;
  // Full detail (detail endpoint)
  maintenance_intervals?: MaintenanceInterval[];
  /** From the type's registry row; read through categoryOf. */
  equipment_category?: EquipmentCategory | null;
  // Current custody, if the machine is issued to somebody
  assignment_id?: number | null;
  assigned_user_id?: number | null;
  assigned_user_name?: string | null;
  assigned_at?: string | null;
  /** Null on a standing assignment; a date on a loan. */
  assigned_due_back_on?: string | null;
  /** The photo shown on the list card and detail header; null when none. */
  primary_photo_id?: number | null;
  created_at: string;
  updated_at: string;
}

// ─── Photos ──────────────────────────────────────────────────────────────────

export interface EquipmentPhoto {
  id: number;
  equipment_id: number;
  file_name: string | null;
  mime_type: string | null;
  caption: string | null;
  is_primary: boolean;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  created_at: string;
}

// ─── Custody ─────────────────────────────────────────────────────────────────

export interface EquipmentAssignment {
  id: number;
  equipment_id: number;
  user_id: number;
  user_name: string | null;
  assigned_at: string;
  /** Null = standing assignment. */
  due_back_on: string | null;
  returned_at: string | null;
  notes: string | null;
  return_notes: string | null;
  assigned_by_name: string | null;
  returned_by_name: string | null;
}

export interface AssignEquipmentPayload {
  user_id: number;
  due_back_on?: string | null;
  notes?: string | null;
}

/** A row of GET /equipment/mine — the machine plus the terms it is held on. */
export interface HeldEquipment {
  assignment_id: number;
  assigned_at: string;
  due_back_on: string | null;
  overdue: boolean;
  notes: string | null;
  id: number;
  name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  make?: string | null;
  model?: string | null;
  status: EquipmentStatus;
  meter_type: MeterType;
  current_hours?: Numeric;
  current_km?: Numeric;
  is_implement: boolean;
  farm_name?: string | null;
  maintenance_due_count: number;
}

// ─── Relay run time (pumps linked to an irrigation node) ─────────────────────

export interface RelayRun {
  id: number;
  started_at: string;
  ended_at: string | null;
  credited_hours: NumericValue | null;
  capped: boolean;
}

export interface EquipmentRuntime {
  link: {
    node_id: number;
    node_name: string | null;
    source_subtype: string | null;
    relay_router_mac: string | null;
    relay_index: number | null;
    farm_id: number;
    farm_name: string;
  } | null;
  running_since: string | null;
  runs: RelayRun[];
}

export interface EquipmentFormData {
  name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  /** Only read when equipment_type is 'custom': what the new type is. */
  custom_type_category?: EquipmentCategory | null;
  make?: string | null;
  model?: string | null;
  serial_number?: string | null;
  year_of_manufacture?: number | null;
  purchase_date?: string | null;
  purchase_price?: number | null;
  status?: EquipmentStatus;
  location_name?: string | null;
  farm_id?: number | null;
  notes?: string | null;
  meter_type: MeterType;
  current_hours?: number | null;
  current_km?: number | null;
  can_tow_implements?: boolean;
  is_implement?: boolean;
  working_width_m?: number | null;
  // Spray calibration
  tank_capacity_litres?: number | null;
  nozzle_type?: string | null;
  nozzle_spacing_cm?: number | null;
  boom_width_m?: number | null;
  operating_pressure_bar?: number | null;
  operating_speed_kph?: number | null;
  output_rate_lpm?: number | null;
  output_rate_lpha?: number | null;
  calibration_date?: string | null;
  calibration_notes?: string | null;
  // Road-vehicle fields
  vin?: string | null;
  rego_number?: string | null;
  rego_state?: AuRegoState | null;
  rego_expiry?: string | null;
  engine_number?: string | null;
  engine_code?: string | null;
  engine_capacity_l?: number | null;
  fuel_type?: FuelType | null;
  transmission?: TransmissionType | null;
  drive_type?: DriveType | null;
  body_type?: string | null;
  colour?: string | null;
}

// ─── Attachments ─────────────────────────────────────────────────────────────

export interface EquipmentAttachment {
  attachment_id: number;
  prime_mover_id: number;
  implement_id: number;
  attached_at: string;
  attached_by?: number | null;
  detached_at?: string | null;
  detached_by?: number | null;
  notes?: string | null;
  prime_mover_name?: string;
  prime_mover_type?: string;
  implement_name?: string;
  implement_type?: string;
  meter_type?: MeterType;
  current_hours?: Numeric;
  current_km?: Numeric;
  implement_status?: EquipmentStatus;
}

// ─── Maintenance Intervals ────────────────────────────────────────────────────

export interface MaintenanceInterval {
  id: number;
  organisation_id: number;
  equipment_id: number;
  name: string;
  interval_type: IntervalType;
  interval_value: NumericValue;
  alert_before: NumericValue;
  last_done_hours?: Numeric;
  last_done_km?: Numeric;
  last_done_date?: string | null;
  next_due_hours?: Numeric;
  next_due_km?: Numeric;
  next_due_date?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Computed (due endpoint)
  current_hours?: Numeric;
  current_km?: Numeric;
}

export interface MaintenanceIntervalFormData {
  name: string;
  interval_type: IntervalType;
  interval_value: number;
  alert_before?: number;
  last_done_hours?: number | null;
  last_done_km?: number | null;
  last_done_date?: string | null;
}

// ─── Usage Sessions ───────────────────────────────────────────────────────────

export interface SessionImplement {
  id: number;
  session_id: number;
  equipment_id: number;
  name: string;
  equipment_type: string;
  custom_type_label?: string | null;
  meter_type: MeterType;
  hours_start?: Numeric;
  hours_end?: Numeric;
}

export interface UsageSession {
  id: number;
  organisation_id: number;
  primary_equipment_id: number;
  operator_id: number;
  task_description?: string | null;
  farm_id?: number | null;
  farm_name?: string | null;
  status: SessionStatus;
  hours_start?: Numeric;
  hours_end?: Numeric;
  km_start?: Numeric;
  km_end?: Numeric;
  spray_application_id?: number | null;
  spray_reference_number?: string | null;
  /** Linked farm job — present for all sessions created by starting a job. */
  job_id?: number | null;
  job_name?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  // Joined
  equipment_name?: string;
  equipment_type?: string;
  meter_type?: MeterType;
  operator_first_name?: string;
  operator_last_name?: string;
  implements?: SessionImplement[];
  // GPS
  gps_track_id?: number | null;
  total_distance_km?: Numeric;
  gps_point_count?: number;
}

export interface SessionFormData {
  task_description?: string;
  farm_id?: number | null;
  hours_start?: number | null;
  km_start?: number | null;
  implement_ids?: number[];
}

export interface CompleteSessionData {
  hours_end?: number | null;
  km_end?: number | null;
  task_description?: string;
  maintenance_done_ids?: number[];
}

// ─── GPS ──────────────────────────────────────────────────────────────────────

export interface GpsPoint {
  lat: number;
  lng: number;
  ts?: string;
  speed?: number | null;
  accuracy?: number | null;
}

export interface GpsTrack {
  id: number;
  organisation_id: number;
  session_id: number;
  points: GpsPoint[];
  total_distance_km?: Numeric;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Health Summary (dashboard card) ─────────────────────────────────────────

export type FlagReason =
  | 'under_repair'
  | 'maintenance_due'
  | 'overdue_service'
  | 'due_this_week'
  | 'rego_expired'
  | 'rego_expiring'
  | 'insurance_expired'
  | 'insurance_expiring'
  | 'insurance_none';

export interface EquipmentHealthFlaggedItem {
  id: number;
  name: string;
  equipment_type: string;
  status: EquipmentStatus;
  reason: FlagReason;
  meter_type: MeterType;
  current_hours?: Numeric;
  current_km?: Numeric;
  maintenance_due_count: number;
  next_service_date?: string | null;
  overdue_days?: number | null;
  rego_number?: string | null;
  rego_expiry?: string | null;
  /** Days until the rego expires; negative once it has. */
  rego_days_left?: number | null;
  /** The machine's current insurance policy, if it is on one. */
  insurance_policy_id?: number | null;
  insurance_expiry?: string | null;
  /** Days until that policy expires; negative once it has. */
  insurance_days_left?: number | null;
}

export interface EquipmentHealthSummary {
  total_active: number;
  under_repair: number;
  overdue_service: number;
  due_this_week: number;
  maintenance_due: number;
  rego_expired: number;
  /** Now counts the same 7-day window as rego_expiring_7d; kept for older bundles. */
  rego_expiring_30d: number;
  /** Absent from a server older than the 7-day rego warning. */
  rego_expiring_7d?: number;
  // Per machine. Absent from a server older than equipment insurance.
  insurance_expired?: number;
  /** Within 30 days, the reminder's first window. */
  insurance_expiring?: number;
  /** Road vehicles on no current policy, once the org records insurance at all. */
  insurance_none?: number;
  flagged: EquipmentHealthFlaggedItem[];
}

// ─── Checklists ───────────────────────────────────────────────────────────────

export interface ChecklistTemplateItem {
  id: number;
  template_id: number;
  sort_order: number;
  item_text: string;
  is_required: boolean;
}

export interface ChecklistTemplate {
  id: number;
  organisation_id: number;
  name: string;
  equipment_type?: string | null;
  custom_type_label?: string | null;
  description?: string | null;
  is_active: boolean;
  items: ChecklistTemplateItem[];
  created_at: string;
  updated_at: string;
}

export interface ChecklistTemplatePayload {
  name: string;
  equipment_type?: string | null;
  description?: string | null;
  is_active?: boolean;
  items: Array<{ item_text: string; is_required: boolean; sort_order: number }>;
}

export interface EquipmentServiceType {
  id: number;
  organisation_id: number;
  name: string;
  description?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PreStartCheckItem {
  type: 'template' | 'maintenance';
  template_item_id?: number;      // for template items
  interval_id?: number;           // for maintenance items
  item_text: string;
  is_required: boolean;
  is_overdue?: boolean;           // maintenance items only
  interval_type?: IntervalType;   // maintenance items only
  next_due_hours?: Numeric;
  next_due_km?: Numeric;
  next_due_date?: string | null;
}

// ─── Service Records ──────────────────────────────────────────────────────────

export interface ServiceRecord {
  id: number;
  organisation_id: number;
  equipment_id: number;
  equipment_name?: string;
  service_type_id?: number | null;
  service_type_name?: string | null;
  title: string;
  description?: string | null;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  hours_at_service?: Numeric;
  assigned_to?: number | null;
  assigned_to_first_name?: string | null;
  assigned_to_last_name?: string | null;
  signed_off_by?: number | null;
  technician_notes?: string | null;
  equipment_condition?: string | null;
  next_service_date?: string | null;
  next_service_hours?: Numeric;
  labour_hours?: Numeric;
  labour_cost?: Numeric;
  total_parts_cost?: Numeric;
  parts_lines?: ServiceRecordPartLine[];
  created_at: string;
  updated_at: string;
}

// ─── Service record parts (line items) ──────────────────────────────────────

export interface ServiceRecordPartLine {
  id: number;
  organisation_id: number;
  service_record_id: number;
  part_id?: number | null;
  description?: string | null;
  quantity: NumericValue;
  unit?: string | null;
  unit_cost?: Numeric;
  total_cost?: Numeric;
  notes?: string | null;
  created_at: string;
  // Joined from parts
  part_brand?: string | null;
  part_number?: string | null;
  part_category?: string | null;
  part_is_oem?: boolean | null;
}

export interface ServiceRecordPartLineInput {
  part_id?: number | null;
  description?: string | null;
  quantity: number;
  unit?: string | null;
  unit_cost?: number | null;
  notes?: string | null;
}
